import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FiArrowLeft,
  FiCalendar,
  FiCreditCard,
  FiFileText,
  FiHash,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
  FiUser,
} from 'react-icons/fi';
import { MdDescription, MdPictureAsPdf, MdPreview } from 'react-icons/md';
import InvoiceEditorSection from './components/InvoiceEditorSection';
import PaymentInvoiceTemplatePreview from './components/PaymentInvoiceTemplatePreview';
import Snackbar from '../Admin-Subscriptions/components/Snackbar';
import { legacyCustomerApi, getApiErrorMessage } from '../subscriptionAdmin/api';
import { filterSubscriptionCustomers } from '../subscriptionAdmin/userDisplay';
import {
  billToFromUser,
  computePaymentInvoiceTotals,
  createDefaultPaymentInvoiceModel,
  createEmptyPaymentLineItem,
  formatGbp,
  formatPaymentInvoiceDate,
  generatePaymentInvoiceNumber,
  paymentLineAmount,
} from './utils/paymentInvoiceModelDefaults';
import {
  downloadPaymentInvoicePdf,
  downloadPaymentInvoiceWord,
  paymentInvoiceFileBaseName,
} from './utils/paymentInvoiceExport';
import {
  fieldSx,
  ghostButtonSx,
  INVOICE_THEME,
  primaryButtonSx,
} from './utils/invoiceDesignTokens';

const PAYMENT_ACCENT = '#0d9488';

function updateLineItem(items, id, field, value) {
  return items.map((item) => (item.id === id ? { ...item, [field]: value } : item));
}

function customerLabel(user) {
  const name = user.organizationName || user.name || user.email || 'Customer';
  const email = user.email ? ` · ${user.email}` : '';
  return `${name}${email}`;
}

export default function CreatePaymentInvoicePage() {
  const previewRef = useRef(null);
  const [model, setModel] = useState(createDefaultPaymentInvoiceModel);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [generating, setGenerating] = useState({ pdf: false, word: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });

  const { total, inrAmount } = computePaymentInvoiceTotals(model);
  const patchModel = (fields) => setModel((prev) => ({ ...prev, ...fields }));
  const busy = generating.pdf || generating.word;

  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const users = await legacyCustomerApi.listUsers();
      setCustomers(filterSubscriptionCustomers(users));
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleCustomerChange = (userId) => {
    const user = customers.find((c) => String(c._id) === String(userId));
    const billTo = billToFromUser(user);
    patchModel({
      selectedUserId: userId,
      billToName: billTo.billToName,
      billToAddress: billTo.billToAddress,
    });
  };

  const addLineItem = () => {
    setModel((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, createEmptyPaymentLineItem()],
    }));
  };

  const removeLineItem = (id) => {
    setModel((prev) => ({
      ...prev,
      lineItems: prev.lineItems.length > 1 ? prev.lineItems.filter((item) => item.id !== id) : prev.lineItems,
    }));
  };

  const resetModel = () => setModel(createDefaultPaymentInvoiceModel());

  const handleDownloadPdf = async () => {
    setGenerating((prev) => ({ ...prev, pdf: true }));
    try {
      await downloadPaymentInvoicePdf(previewRef.current, `${paymentInvoiceFileBaseName(model)}.pdf`);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: err?.message || 'Failed to generate PDF.' });
    } finally {
      setGenerating((prev) => ({ ...prev, pdf: false }));
    }
  };

  const handleDownloadWord = () => {
    setGenerating((prev) => ({ ...prev, word: true }));
    try {
      downloadPaymentInvoiceWord(model, `${paymentInvoiceFileBaseName(model)}.doc`);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: err?.message || 'Failed to generate Word document.' });
    } finally {
      setGenerating((prev) => ({ ...prev, word: false }));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pb: 6,
        background: `
          radial-gradient(ellipse 80% 50% at 15% -10%, rgba(13,148,136,0.12), transparent),
          radial-gradient(ellipse 60% 40% at 95% 0%, rgba(20,184,166,0.1), transparent),
          #f1f5f9
        `,
      }}
    >
      <Box
        sx={{
          borderBottom: `1px solid ${INVOICE_THEME.border}`,
          background: 'rgba(255,255,255,0.78)',
          backdropFilter: 'blur(16px)',
          position: 'sticky',
          top: 72,
          zIndex: 20,
        }}
      >
        <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
          <Button
            component={RouterLink}
            to="/invoice"
            startIcon={<FiArrowLeft />}
            sx={{ ...ghostButtonSx, mb: 1.5, py: 0.5, minWidth: 0 }}
          >
            Invoice hub
          </Button>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: 'clamp(1.6rem, 3vw, 2.1rem)',
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    color: INVOICE_THEME.ink,
                  }}
                >
                  Create Payment Invoice
                </Typography>
                <Chip
                  label="GBP · Export billing"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: 'rgba(13,148,136,0.1)',
                    color: PAYMENT_ACCENT,
                    border: '1px solid rgba(13,148,136,0.22)',
                  }}
                />
              </Box>
              <Typography sx={{ color: INVOICE_THEME.slate, maxWidth: 560, fontSize: '0.92rem' }}>
                Build a polished export invoice with Spotless AI defaults, pick a customer for Bill To, and download PDF or Word.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: '14px',
                  bgcolor: '#fff',
                  border: `1px solid ${INVOICE_THEME.border}`,
                  boxShadow: INVOICE_THEME.shadowSoft,
                }}
              >
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: INVOICE_THEME.slate, letterSpacing: '0.08em' }}>
                  BALANCE DUE
                </Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: PAYMENT_ACCENT }}>
                  {formatGbp(total)}
                </Typography>
                {inrAmount > 0 && (
                  <Typography sx={{ fontSize: '0.72rem', color: INVOICE_THEME.slate, mt: 0.25 }}>
                    ≈ ₹{inrAmount.toLocaleString('en-IN')}
                  </Typography>
                )}
              </Box>

              <Button
                sx={{
                  ...primaryButtonSx,
                  background: `linear-gradient(135deg, ${PAYMENT_ACCENT} 0%, #14b8a6 100%)`,
                  '&:hover': { background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)' },
                }}
                startIcon={generating.pdf ? <CircularProgress size={16} color="inherit" /> : <MdPictureAsPdf />}
                onClick={handleDownloadPdf}
                disabled={busy}
              >
                PDF
              </Button>
              <Button
                sx={ghostButtonSx}
                variant="outlined"
                startIcon={generating.word ? <CircularProgress size={16} /> : <MdDescription />}
                onClick={handleDownloadWord}
                disabled={busy}
              >
                Word
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 3 }, pt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 200 },
                display: 'grid',
                gap: 2,
                maxHeight: { lg: 'calc(100vh - 220px)' },
                overflowY: { lg: 'auto' },
                pr: { lg: 0.5 },
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'rgba(100,116,139,0.35)',
                  borderRadius: 999,
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  startIcon={<FiRefreshCw />}
                  onClick={resetModel}
                  sx={{ ...ghostButtonSx, fontSize: '0.8rem' }}
                >
                  Reset form
                </Button>
              </Box>

              <InvoiceEditorSection
                icon={<FiHash size={18} />}
                title="Invoice details"
                subtitle="Number and issue date (today by default)"
              >
                <TextField
                  label="Invoice number"
                  value={model.invoiceNumber}
                  onChange={(e) => patchModel({ invoiceNumber: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Issue date"
                  type="date"
                  value={model.issueDate}
                  onChange={(e) => patchModel({ issueDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={fieldSx}
                />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => patchModel({ invoiceNumber: generatePaymentInvoiceNumber(1) })}
                  sx={{ justifySelf: 'start', textTransform: 'none', fontWeight: 600 }}
                >
                  Regenerate invoice number
                </Button>
              </InvoiceEditorSection>

              <InvoiceEditorSection
                icon={<FiUser size={18} />}
                title="Bill To"
                subtitle="Select a customer — address fills automatically"
              >
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel id="payment-invoice-customer-label">Customer</InputLabel>
                  <Select
                    labelId="payment-invoice-customer-label"
                    label="Customer"
                    value={model.selectedUserId || ''}
                    onChange={(e) => handleCustomerChange(e.target.value)}
                    disabled={loadingCustomers}
                  >
                    <MenuItem value="">
                      <em>{loadingCustomers ? 'Loading customers…' : 'Select customer'}</em>
                    </MenuItem>
                    {customers.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {customerLabel(c)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Bill To name"
                  value={model.billToName}
                  onChange={(e) => patchModel({ billToName: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Bill To address"
                  value={model.billToAddress}
                  onChange={(e) => patchModel({ billToAddress: e.target.value })}
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  sx={fieldSx}
                  placeholder="Customer billing address"
                />
              </InvoiceEditorSection>

              <InvoiceEditorSection
                icon={<FiFileText size={18} />}
                title="Line items"
                subtitle="Duration × daily rate (GBP)"
                action={
                  <Button
                    size="small"
                    startIcon={<FiPlus size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      addLineItem();
                    }}
                    sx={{ ...ghostButtonSx, py: 0.4, minWidth: 0, fontSize: '0.78rem' }}
                  >
                    Add
                  </Button>
                }
              >
                <Box sx={{ display: 'grid', gap: 1.25 }}>
                  {model.lineItems.map((item, index) => (
                    <Box
                      key={item.id}
                      sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        bgcolor: '#f8fafc',
                        border: `1px solid ${INVOICE_THEME.border}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: INVOICE_THEME.slate }}>
                          ITEM {index + 1} · {formatGbp(paymentLineAmount(item))}
                        </Typography>
                        <Tooltip title="Remove row">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => removeLineItem(item.id)}
                              disabled={model.lineItems.length <= 1}
                              sx={{ color: '#94a3b8' }}
                            >
                              <FiTrash2 size={14} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <TextField
                        label="Description"
                        value={item.description}
                        onChange={(e) =>
                          setModel((prev) => ({
                            ...prev,
                            lineItems: updateLineItem(prev.lineItems, item.id, 'description', e.target.value),
                          }))
                        }
                        fullWidth
                        size="small"
                        sx={{ ...fieldSx, mb: 1.25 }}
                      />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                        <TextField
                          label="Duration (days)"
                          type="number"
                          value={item.durationDays}
                          onChange={(e) =>
                            setModel((prev) => ({
                              ...prev,
                              lineItems: updateLineItem(prev.lineItems, item.id, 'durationDays', e.target.value),
                            }))
                          }
                          size="small"
                          sx={fieldSx}
                          inputProps={{ min: 0, step: '1' }}
                        />
                        <TextField
                          label="Rate (GBP/day)"
                          type="number"
                          value={item.ratePerDay}
                          onChange={(e) =>
                            setModel((prev) => ({
                              ...prev,
                              lineItems: updateLineItem(prev.lineItems, item.id, 'ratePerDay', e.target.value),
                            }))
                          }
                          size="small"
                          sx={fieldSx}
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </InvoiceEditorSection>

              <InvoiceEditorSection
                icon={<FiCreditCard size={18} />}
                title="INR equivalent"
                subtitle="For amount-in-words on the invoice"
                defaultOpen={false}
              >
                <TextField
                  label="GBP → INR exchange rate"
                  type="number"
                  value={model.exchangeRate}
                  onChange={(e) => patchModel({ exchangeRate: e.target.value })}
                  size="small"
                  sx={fieldSx}
                  inputProps={{ min: 0, step: '0.001' }}
                  helperText={inrAmount > 0 ? `Computed INR: ₹${inrAmount.toLocaleString('en-IN')}` : 'Enter a rate to show INR value'}
                />
              </InvoiceEditorSection>

              <InvoiceEditorSection
                icon={<FiCalendar size={18} />}
                title="Issuer & bank"
                subtitle="Spotless AI defaults — edit if needed"
                defaultOpen={false}
              >
                <TextField
                  label="Authorized signatory"
                  value={model.authorizedSignatory}
                  onChange={(e) => patchModel({ authorizedSignatory: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Company name"
                  value={model.companyName}
                  onChange={(e) => patchModel({ companyName: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Company address"
                  value={model.companyAddress}
                  onChange={(e) => patchModel({ companyAddress: e.target.value })}
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="GSTIN"
                  value={model.companyGstin}
                  onChange={(e) => patchModel({ companyGstin: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Bank name"
                  value={model.bankName}
                  onChange={(e) => patchModel({ bankName: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Account number"
                  value={model.accountNumber}
                  onChange={(e) => patchModel({ accountNumber: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Branch & IFS code"
                  value={model.branchIfsc}
                  onChange={(e) => patchModel({ branchIfsc: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Swift code"
                  value={model.swiftCode}
                  onChange={(e) => patchModel({ swiftCode: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
              </InvoiceEditorSection>
            </Box>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 200 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'rgba(13,148,136,0.1)',
                      color: PAYMENT_ACCENT,
                    }}
                  >
                    <MdPreview size={20} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: INVOICE_THEME.ink, fontSize: '0.95rem' }}>
                      Live preview
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: INVOICE_THEME.slate }}>
                      Logo, title, and Bill To update as you edit
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={formatPaymentInvoiceDate(model.issueDate)}
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: '#fff' }}
                />
              </Box>

              <Box
                sx={{
                  p: { xs: 1.5, md: 3 },
                  borderRadius: `${INVOICE_THEME.radiusLg}px`,
                  background: 'radial-gradient(circle at 1px 1px, rgba(148,163,184,0.18) 1px, transparent 0)',
                  backgroundSize: '20px 20px',
                  bgcolor: INVOICE_THEME.canvas,
                  border: `1px solid ${INVOICE_THEME.border}`,
                }}
              >
                <Box ref={previewRef}>
                  <PaymentInvoiceTemplatePreview model={model} />
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: `${INVOICE_THEME.radius}px`,
                  bgcolor: '#fff',
                  border: `1px solid ${INVOICE_THEME.border}`,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  boxShadow: INVOICE_THEME.shadowSoft,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, color: INVOICE_THEME.ink }}>
                    Ready to share?
                  </Typography>
                  <Typography sx={{ fontSize: '0.84rem', color: INVOICE_THEME.slate }}>
                    Export a print-ready PDF or editable Word document.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  <Button
                    sx={{
                      ...primaryButtonSx,
                      px: 3,
                      background: `linear-gradient(135deg, ${PAYMENT_ACCENT} 0%, #14b8a6 100%)`,
                    }}
                    startIcon={generating.pdf ? <CircularProgress size={16} color="inherit" /> : <MdPictureAsPdf />}
                    onClick={handleDownloadPdf}
                    disabled={busy}
                  >
                    {generating.pdf ? 'Generating…' : 'Download PDF'}
                  </Button>
                  <Button
                    sx={{ ...ghostButtonSx, px: 3 }}
                    variant="outlined"
                    startIcon={generating.word ? <CircularProgress size={16} /> : <MdDescription />}
                    onClick={handleDownloadWord}
                    disabled={busy}
                  >
                    {generating.word ? 'Generating…' : 'Download Word'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </Box>
  );
}
